import { errorToast } from "@/v2-ui/toast"
import {
  type ConfirmButtonProps,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import { useUserMultiSessionMutation } from "@/v2-backoffice-client/user/multiSession/user.multiSession.mutation"

const ALLOWED_CONTENT =
  "Re-subject this user to the single-active-session limit."
const DISALLOWED_CONTENT =
  "Exempt this user from the single-active-session limit so they can stay signed in on several devices (e.g. staff)."

const ALLOWED_CONFIRM =
  "Disallow multi-session for this user? Single-active-session enforcement applies to them again."
const DISALLOWED_CONFIRM =
  "Allow multi-session for this user? They will be exempt from single-active-session enforcement."

export type UserMultiSessionButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm" | "content" | "confirmContent"
> & {
  userId: number
  /** Current exemption state; the button toggles it. */
  isAllowed: boolean
  onCompleted?(): void
}

// Exempts a user from single-active-session enforcement, or puts them back
// under it. The mutation returns Id + MultiSessionAllowed, so Apollo merges
// the new value into the cached user and the caller's isAllowed follows.
function UserMultiSessionButton(props: UserMultiSessionButtonProps) {
  const { userId, isAllowed, onCompleted, children, ...buttonProps } = props

  const [setMultiSession, { loading, error }] = useUserMultiSessionMutation({
    onCompleted,
  })
  errorToast(error, { title: "Failed to update multi-session exemption" })

  return (
    <ConfirmButton
      variant={isAllowed ? "warning" : "success"}
      iconSrc="devices"
      content={isAllowed ? ALLOWED_CONTENT : DISALLOWED_CONTENT}
      confirmContent={isAllowed ? ALLOWED_CONFIRM : DISALLOWED_CONFIRM}
      confirmVariant={isAllowed ? "warning" : "success"}
      isLoading={loading}
      onConfirm={() => {
        setMultiSession({ variables: { userId, allowed: !isAllowed } })
      }}
      {...buttonProps}
    >
      {children ?? (isAllowed ? "Disallow Multi" : "Allow Multi")}
    </ConfirmButton>
  )
}

export default UserMultiSessionButton
